/**
 * Distance Cache Utility
 * Caches distance and duration data for cafe locations to avoid excessive API calls
 * Cache expires after 5 minutes
 */

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

class DistanceCache {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Generate a cache key for a cafe
   * @param {string} cafeId - Unique identifier for the cafe
   * @param {object} userLocation - User's location (Google Maps LatLng object or {lat, lng})
   * @returns {string} Cache key
   */
  _generateKey(cafeId, userLocation) {
    if (!userLocation) return null;

    // Handle Google Maps LatLng object (has lat() and lng() methods)
    // or plain object with lat/lng properties
    let lat, lng;

    if (typeof userLocation.lat === "function") {
      // Google Maps LatLng object
      lat = userLocation.lat().toFixed(4);
      lng = userLocation.lng().toFixed(4);
    } else {
      // Plain object with lat/lng properties - validate before using
      const numLat = Number(userLocation.lat);
      const numLng = Number(userLocation.lng);

      // Return null if lat or lng is missing, null, undefined, or not a valid number
      if (
        userLocation.lat == null ||
        userLocation.lng == null ||
        isNaN(numLat) ||
        isNaN(numLng)
      ) {
        return null;
      }

      lat = numLat.toFixed(4);
      lng = numLng.toFixed(4);
    }

    return `${cafeId}_${lat}_${lng}`;
  }

  /**
   * Get cached distance data if available and not expired
   * @param {string} cafeId - Unique identifier for the cafe
   * @param {object} userLocation - User's location
   * @returns {object|null} Cached data or null if not available/expired
   */
  get(cafeId, userLocation) {
    const key = this._generateKey(cafeId, userLocation);
    if (!key) return null;

    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    const age = now - cached.timestamp;

    // Check if cache is still valid (less than 5 minutes old)
    if (age < CACHE_DURATION) {
      console.log(
        `[DistanceCache] Cache HIT for ${cafeId} (age: ${Math.round(
          age / 1000
        )}s)`
      );
      return {
        distance: cached.distance,
        duration: cached.duration,
      };
    }

    // Cache expired, remove it
    console.log(
      `[DistanceCache] Cache EXPIRED for ${cafeId} (age: ${Math.round(
        age / 1000
      )}s)`
    );
    this.cache.delete(key);
    return null;
  }

  /**
   * Set cached distance data
   * @param {string} cafeId - Unique identifier for the cafe
   * @param {object} userLocation - User's location
   * @param {string} distance - Distance text (e.g., "5.2 km")
   * @param {string} duration - Duration text (e.g., "12 mins")
   */
  set(cafeId, userLocation, distance, duration) {
    const key = this._generateKey(cafeId, userLocation);
    if (!key) return;

    this.cache.set(key, {
      distance,
      duration,
      timestamp: Date.now(),
    });
    console.log(
      `[DistanceCache] Cached distance for ${cafeId}: ${distance}, ${duration}`
    );
  }

  /**
   * Clear all cached data
   */
  clear() {
    console.log("[DistanceCache] Clearing all cached distances");
    this.cache.clear();
  }

  /**
   * Clear expired entries from cache
   */
  cleanup() {
    const now = Date.now();
    let removed = 0;

    for (const [key, value] of this.cache.entries()) {
      const age = now - value.timestamp;
      if (age >= CACHE_DURATION) {
        this.cache.delete(key);
        removed++;
      }
    }

    if (removed > 0) {
      console.log(
        `[DistanceCache] Cleaned up ${removed} expired cache entries`
      );
    }
  }
}

// Export singleton instance
export const distanceCache = new DistanceCache();

// Timer management to prevent leaks in tests/module reloads
let cleanupIntervalId = null;

/**
 * Start automatic cleanup of expired cache entries
 * @param {number} intervalMs - Cleanup interval in milliseconds (default: 60000 = 1 minute)
 */
export function startCleanup(intervalMs = 60000) {
  if (cleanupIntervalId !== null) {
    console.warn("[DistanceCache] Cleanup already running");
    return;
  }

  cleanupIntervalId = setInterval(() => {
    distanceCache.cleanup();
  }, intervalMs);

  console.log(`[DistanceCache] Started cleanup interval (${intervalMs}ms)`);
}

/**
 * Stop automatic cleanup (useful for tests and teardown)
 */
export function stopCleanup() {
  if (cleanupIntervalId !== null) {
    clearInterval(cleanupIntervalId);
    cleanupIntervalId = null;
    console.log("[DistanceCache] Stopped cleanup interval");
  }
}

// Auto-start cleanup by default (can be stopped with stopCleanup() if needed)
startCleanup();
